import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';

const EquipmentTab = ({ tripData, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    hasStove: tripData?.hasStove ?? false,
    hasOven: tripData?.hasOven ?? false,
    hasGrill: tripData?.hasGrill ?? false,
    hasMicrowave: tripData?.hasMicrowave ?? false,
    hasAirFryer: tripData?.hasAirFryer ?? false
  });
  const [errors, setErrors] = useState('');
  const [saving, setSaving] = useState(false);

  const handleChange = (name, checked) => {
    setFormData(prev => ({ ...prev, [name]: checked }));
    setErrors('');
  };

  const validate = () => {
    const hasAny = Object.values(formData)?.some(val => val === true);
    if (!hasAny) {
      setErrors('At least one cooking equipment must be selected');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setSaving(true);
    const result = await onUpdate(formData);
    setSaving(false);
    
    if (result?.success) {
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      hasStove: tripData?.hasStove ?? false,
      hasOven: tripData?.hasOven ?? false,
      hasGrill: tripData?.hasGrill ?? false,
      hasMicrowave: tripData?.hasMicrowave ?? false,
      hasAirFryer: tripData?.hasAirFryer ?? false
    });
    setErrors('');
    setIsEditing(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold" style={{ color: 'var(--color-text-primary)' }}>
          Cooking Equipment
        </h2>
        {!isEditing && (
          <Button
            onClick={() => setIsEditing(true)}
            variant="outline"
            size="sm"
          >
            <Icon name="Edit" size={16} />
            <span className="ml-2">Edit</span>
          </Button>
        )}
      </div>

      <p className="text-sm text-muted-foreground">
        Select available galley equipment. This determines which meal templates are available for your trip.
      </p>

      {errors && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
          <p className="text-sm text-red-500">{errors}</p>
        </div>
      )}

      <div className="space-y-4">
        <div className="flex items-start space-x-3 p-4 rounded-lg" style={{ backgroundColor: 'var(--color-background)' }}>
          <Checkbox
            id="hasStove"
            checked={formData?.hasStove}
            onCheckedChange={(checked) => handleChange('hasStove', checked)}
            disabled={!isEditing}
          />
          <div className="flex-1">
            <label htmlFor="hasStove" className="text-sm font-medium cursor-pointer" style={{ color: 'var(--color-text-primary)' }}>
              Stove/Cooktop
            </label>
            <p className="text-xs text-muted-foreground mt-1">
              Gas or electric burners for boiling, sautéing, and pan cooking
            </p>
          </div>
        </div>

        <div className="flex items-start space-x-3 p-4 rounded-lg" style={{ backgroundColor: 'var(--color-background)' }}>
          <Checkbox
            id="hasOven"
            checked={formData?.hasOven}
            onCheckedChange={(checked) => handleChange('hasOven', checked)}
            disabled={!isEditing}
          />
          <div className="flex-1">
            <label htmlFor="hasOven" className="text-sm font-medium cursor-pointer" style={{ color: 'var(--color-text-primary)' }}>
              Oven
            </label>
            <p className="text-xs text-muted-foreground mt-1">
              Baking and roasting capabilities for breads, casseroles, and baked dishes
            </p>
          </div>
        </div>

        <div className="flex items-start space-x-3 p-4 rounded-lg" style={{ backgroundColor: 'var(--color-background)' }}>
          <Checkbox
            id="hasGrill"
            checked={formData?.hasGrill}
            onCheckedChange={(checked) => handleChange('hasGrill', checked)}
            disabled={!isEditing}
          />
          <div className="flex-1">
            <label htmlFor="hasGrill" className="text-sm font-medium cursor-pointer" style={{ color: 'var(--color-text-primary)' }}>
              Grill
            </label>
            <p className="text-xs text-muted-foreground mt-1">
              Outdoor or indoor grilling for meats, vegetables, and seafood
            </p>
          </div>
        </div>

        <div className="flex items-start space-x-3 p-4 rounded-lg" style={{ backgroundColor: 'var(--color-background)' }}>
          <Checkbox
            id="hasMicrowave"
            checked={formData?.hasMicrowave}
            onCheckedChange={(checked) => handleChange('hasMicrowave', checked)}
            disabled={!isEditing}
          />
          <div className="flex-1">
            <label htmlFor="hasMicrowave" className="text-sm font-medium cursor-pointer" style={{ color: 'var(--color-text-primary)' }}>
              Microwave
            </label>
            <p className="text-xs text-muted-foreground mt-1">
              Quick heating and reheating for leftovers and simple meals
            </p>
          </div>
        </div>

        <div className="flex items-start space-x-3 p-4 rounded-lg" style={{ backgroundColor: 'var(--color-background)' }}>
          <Checkbox
            id="hasAirFryer"
            checked={formData?.hasAirFryer}
            onCheckedChange={(checked) => handleChange('hasAirFryer', checked)}
            disabled={!isEditing}
          />
          <div className="flex-1">
            <label htmlFor="hasAirFryer" className="text-sm font-medium cursor-pointer" style={{ color: 'var(--color-text-primary)' }}>
              Air Fryer
            </label>
            <p className="text-xs text-muted-foreground mt-1">
              Convection cooking for crispy foods with minimal oil
            </p>
          </div>
        </div>
      </div>

      {isEditing && (
        <div className="flex gap-3 pt-4">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
          <Button onClick={handleCancel} variant="outline" disabled={saving}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
};

export default EquipmentTab;